import { useState } from "react";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT } from "@/data/content";
import CAMPAIGN from "@/data/campaign";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-transparent border-border rounded-none h-12 focus-visible:ring-0 focus-visible:border-primary transition-colors duration-300 font-light";

const labelCls = "text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground";

const InquiryForm = ({ idPrefix = "lead-form", onSubmitted }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [date, setDate] = useState("");
  const [travellers, setTravellers] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        travel_date: date || null,
        travellers: travellers ? parseInt(travellers, 10) : null,
        campaign: CAMPAIGN.slug,
      });
      toast.success("Journey request received.", {
        description: "Our travel expert will call you within 24 hours.",
      });
      if (window.fbq) window.fbq("track", "Lead", { content_name: CAMPAIGN.name });
      setForm({ name: "", phone: "", email: "" });
      setDate("");
      setTravellers("");
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn't submit right now.", {
        description: typeof detail === "string" ? detail : `Please call us at ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-name`} className={labelCls}>Full name</Label>
          <Input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={set("name")}
            placeholder="Aarav Sharma"
            className={inputCls}
            data-testid={`${idPrefix}-name-input`}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-phone`} className={labelCls}>Phone / WhatsApp</Label>
          <Input
            id={`${idPrefix}-phone`}
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91 98765 43210"
            className={inputCls}
            data-testid={`${idPrefix}-phone-input`}
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-email`} className={labelCls}>Email</Label>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className={inputCls}
            data-testid={`${idPrefix}-email-input`}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-date`} className={labelCls}>Preferred travel date</Label>
          <Input
            id={`${idPrefix}-date`}
            type="date"
            min={new Date().toISOString().split("T")[0]}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`${inputCls} text-foreground`}
            data-testid={`${idPrefix}-date-input`}
          />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Travellers</Label>
          <Select value={travellers} onValueChange={setTravellers}>
            <SelectTrigger className={`${inputCls} w-full`} data-testid={`${idPrefix}-travellers-select`}>
              <SelectValue placeholder="How many of you?" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-border">
              {[["1", "Just me"], ["2", "2 people"], ["3", "3 people"], ["4", "4 people"], ["5", "5 people"], ["6", "6 people"], ["8", "7 - 8 people"], ["12", "9 - 12 people"], ["20", "Big group (13+)"]].map(([v, label]) => (
                <SelectItem key={v} value={v} data-testid={`${idPrefix}-travellers-option-${v}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="mt-10 w-full bg-primary text-primary-foreground hover:bg-[#2E9440] rounded-none h-14 text-sm font-semibold uppercase tracking-[0.2em] transition-colors duration-300"
        data-testid={`${idPrefix}-submit-button`}
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <span className="flex items-center gap-3">
            Request my callback <Send className="w-4 h-4" />
          </span>
        )}
      </Button>
      <p className="mt-4 text-center text-xs font-light text-muted-foreground" data-testid={`${idPrefix}-disclaimer`}>
        By submitting, you agree to be contacted by the TrekPanda team about this trip.
      </p>
    </form>
  );
};

export default InquiryForm;
